/**
 * P1.2: Patient Email Templates UI
 * Dedicated UI for managing PATIENT audience email templates only
 * P2.3b: Full i18n coverage
 * 
 * STRICTLY EVENT-DRIVEN:
 * - Templates rendered ONLY from emailEventDictionary
 * - No "Create New Template" action
 * - Admin can only edit subject/body, NOT event structure
 * 
 * MOCKED - No real email sending. Templates stored in localStorage.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTreatXAdminAuth } from '../../context/TreatXAdminAuthContext';
import { 
  useEmailTemplates, 
  TEMPLATE_VARIABLES,
  EMAIL_AUDIENCE,
  EmailTemplateProvider 
} from '../../context/EmailTemplateContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Mail, ArrowLeft, LogOut, Search, Edit2, Eye, RotateCcw,
  X, AlertTriangle, Clock, User, Info, Copy, CheckCircle,
  Key, CreditCard, UserCheck, MessageSquare, Users, Lock,
  Sparkles, Send, Heart, UserPlus, Search as SearchIcon,
  FileCheck, DollarSign, Shield
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';
import TemplateInlineIndicators from '../../components/admin/TemplateInlineIndicators';
import useTemplateStatusMap from '../../hooks/useTemplateStatusMap';

// Patient-specific category configuration
const PATIENT_CATEGORIES = {
  auth: { 
    icon: Key, 
    color: 'bg-purple-100 text-purple-700',
    borderColor: 'border-purple-200',
    label: 'Authentication',
    description: 'Account creation, verification, and password management'
  },
  intake: { 
    icon: MessageSquare, 
    color: 'bg-cyan-100 text-cyan-700',
    borderColor: 'border-cyan-200',
    label: 'Intake & Inquiry',
    description: 'Treatment inquiry submissions and updates'
  },
  matching: { 
    icon: UserCheck, 
    color: 'bg-emerald-100 text-emerald-700',
    borderColor: 'border-emerald-200',
    label: 'Clinic Matching',
    description: 'Matching patients with clinics based on their needs'
  },
  offer: { 
    icon: FileCheck, 
    color: 'bg-amber-100 text-amber-700',
    borderColor: 'border-amber-200',
    label: 'Offers & Quotes',
    description: 'Treatment offers from clinics'
  },
  payment: { 
    icon: DollarSign, 
    color: 'bg-green-100 text-green-700',
    borderColor: 'border-green-200',
    label: 'Payments & Deposits',
    description: 'Deposit requests and payment confirmations'
  },
};

// Mock patient data for preview
// TREATX-PRODUCTION-P0-OWNER-ACCEPTANCE-03 §P0-5: use self-describing
// `<sample-…>` placeholders so an owner reviewing a preview cannot
// mistake the example token/link/code for a real one issued to a
// production patient. Applied consistently with emailEventDictionary.
const MOCK_PATIENT_DATA = {
  patientName: 'Maria Garcia',
  patientFirstName: 'Maria',
  patientEmail: 'maria.garcia@example.com',
  patientPhone: '+49 170 555 1234',
  patientCountry: 'Germany',
  verificationLink: 'https://treatx.health/verify?token=<sample-verification-token>',
  verificationCode: '485721 (sample)',
  resetPasswordLink: 'https://treatx.health/reset?token=<sample-reset-token>',
  currentDate: 'January 26, 2026',
  intakeId: 'INK-2026-005678',
  treatmentType: 'Rhinoplasty',
  preferredDestination: 'Turkey',
  matchCount: '5',
  matchedClinics: 'Istanbul Aesthetics Clinic, Ankara Medical Center, Izmir Beauty Hospital',
  clinicName: 'Istanbul Aesthetics Clinic',
  offerId: 'OFF-2026-009012',
  offerAmount: '€3,500',
  offerCurrency: 'EUR',
  offerExpiry: 'February 15, 2026',
  depositAmount: '€500',
  depositDueDate: 'February 1, 2026',
  paymentMethod: 'Credit Card (Visa ****1234)',
  supportEmail: 'support@treatx.health',
  platformUrl: 'https://treatx.health',
};

// Get patient-safe variables only
const getPatientVariables = () => {
  return Object.entries(TEMPLATE_VARIABLES)
    .filter(([_, config]) => config.scope?.includes('PATIENT'))
    .map(([key, config]) => ({ key, ...config }));
};

// Template Editor Modal (Patient-specific)
const PatientTemplateEditorModal = ({ template, onClose, onSave }) => {
  const { generatePreview, getUsedVariables, resetToDefault } = useEmailTemplates();
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body);
  const [showPreview, setShowPreview] = useState(true); // Default show preview
  const [isSaving, setIsSaving] = useState(false);
  
  // Get variables specific to this event
  const eventVariables = useMemo(() => {
    return template.defaultVariables
      .filter(varKey => TEMPLATE_VARIABLES[varKey])
      .map(varKey => ({
        key: varKey,
        ...TEMPLATE_VARIABLES[varKey]
      }));
  }, [template.defaultVariables]);
  
  const usedVariables = getUsedVariables({ subject, body });
  
  // Generate preview with patient mock data
  const preview = useMemo(() => {
    let previewSubject = subject;
    let previewBody = body;
    
    Object.entries(MOCK_PATIENT_DATA).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      previewSubject = previewSubject.replace(regex, value);
      previewBody = previewBody.replace(regex, value);
    });
    
    return { subject: previewSubject, body: previewBody };
  }, [subject, body]);
  
  const categoryConfig = PATIENT_CATEGORIES[template.category] || PATIENT_CATEGORIES.auth;
  const CategoryIcon = categoryConfig.icon;
  
  const handleSave = async () => {
    setIsSaving(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    onSave(template.id, { subject, body });
    setIsSaving(false);
    toast({ title: 'Template Saved', description: 'Patient email template has been updated.' });
    onClose();
  };
  
  const handleReset = () => {
    resetToDefault(template.id);
    toast({ title: 'Template Reset', description: 'Template has been reset to default content.' });
    onClose();
  };
  
  const insertVariable = (varKey) => {
    const textArea = document.getElementById('template-body');
    const cursorPos = textArea?.selectionStart || body.length;
    const newBody = body.slice(0, cursorPos) + `{{${varKey}}}` + body.slice(cursorPos);
    setBody(newBody);
    toast({ title: 'Variable Inserted', description: `{{${varKey}}} added to template` });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-5 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <CategoryIcon className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold">Edit: {template.name}</h2>
                  <Badge className="bg-blue-800/50 text-blue-100">
                    <Users className="h-3 w-3 mr-1" />
                    Patient
                  </Badge>
                </div>
                <p className="text-blue-200 text-sm">{template.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300" data-testid="patient-templates-editor-backend-badge">BACKEND-CONNECTED</Badge>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={onClose}
                className="text-white hover:bg-white/20"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
          {/* Event Key (Read-only) */}
          <div className="mt-2 flex items-center gap-2">
            <Lock className="h-3 w-3 text-blue-300" />
            <span className="text-xs text-blue-300">Event Key (immutable):</span>
            <code className="text-xs bg-white/20 px-2 py-0.5 rounded font-mono">
              {template.eventKey}
            </code>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Editor Panel */}
          <div className="flex-1 overflow-y-auto p-6 border-r">
            <div className="space-y-6">
              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Subject
                </label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter email subject..."
                  className="font-medium text-base"
                  data-testid="patient-template-subject"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use variables like {'{{patientFirstName}}'} for personalization
                </p>
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Body
                </label>
                <Textarea
                  id="template-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Enter email body..."
                  rows={18}
                  className="font-mono text-sm"
                  data-testid="patient-template-body"
                />
              </div>

              {/* Variables Panel */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Available Variables for This Event
                </h4>
                <p className="text-xs text-blue-600 mb-3">
                  Click a variable to insert it at your cursor position in the body.
                </p>
                <div className="flex flex-wrap gap-2">
                  {eventVariables.map(({ key, label }) => {
                    const isUsed = usedVariables.includes(key);
                    return (
                      <button
                        key={key}
                        onClick={() => insertVariable(key)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                          isUsed
                            ? 'bg-green-100 border-green-400 text-green-700 ring-2 ring-green-200'
                            : 'bg-white border-blue-300 text-blue-700 hover:bg-blue-100 hover:border-blue-400'
                        }`}
                        title={`${label} - Example: ${MOCK_PATIENT_DATA[key] || 'N/A'}`}
                        data-testid={`var-${key}`}
                      >
                        {isUsed && <CheckCircle className="h-3 w-3 inline mr-1" />}
                        {`{{${key}}}`}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-xs text-blue-600">
                    <span className="inline-flex items-center gap-1">
                      <CheckCircle className="h-3 w-3 text-green-600" />
                      Green = used in template
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Panel */}
          <div className="w-[480px] overflow-y-auto bg-slate-50 p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Live Preview
              </h4>
              <Badge variant="outline" className="text-xs">
                Mock Patient: Maria Garcia
              </Badge>
            </div>
            
            {/* Email Preview Card */}
            <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
              {/* Email Header */}
              <div className="bg-gray-100 p-3 border-b space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-medium w-12">From:</span>
                  <span>TreatX &lt;notifications@treatx.health&gt;</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-medium w-12">To:</span>
                  <span>{MOCK_PATIENT_DATA.patientEmail}</span>
                </div>
                <div className="flex items-start gap-2 text-sm pt-1 border-t border-gray-200 mt-1">
                  <span className="font-medium w-12 text-gray-600">Subject:</span>
                  <span className="font-semibold text-gray-900 flex-1">{preview.subject}</span>
                </div>
              </div>
              
              {/* Email Body */}
              <div className="p-4 max-h-[400px] overflow-y-auto">
                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed">
                  {preview.body}
                </pre>
              </div>
            </div>

            {/* Mock Data Reference */}
            <div className="mt-4 p-3 bg-white rounded-lg border">
              <p className="text-xs font-medium text-gray-500 mb-2">Mock Data Used:</p>
              <div className="space-y-1 text-xs">
                {eventVariables.slice(0, 5).map(({ key, label }) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-gray-500">{label}:</span>
                    <span className="text-gray-700 font-mono">{MOCK_PATIENT_DATA[key] || 'N/A'}</span>
                  </div>
                ))}
                {eventVariables.length > 5 && (
                  <p className="text-gray-400 italic">+ {eventVariables.length - 5} more...</p>
                )}
              </div>
            </div>
            
            {/* Sample-preview note (TREATX-PRODUCTION-P0-OWNER-ACCEPTANCE-02 §P0-3) */}
            <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-start gap-2" data-testid="patient-template-preview-mode-note">
              <Info className="h-4 w-4 text-slate-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-slate-700">
                <strong>Sample preview</strong> — variables are filled with example values from the dictionary
                (e.g. <code className="px-1 bg-slate-100 rounded">&lt;sample-verification-token&gt;</code>). Use
                <strong> Send test email</strong> below for a real end-to-end send via the canonical NotificationService.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleReset}
              className="text-amber-600 border-amber-300 hover:bg-amber-50"
              disabled={template.isDefault}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Default
            </Button>
            {template.lastModified && (
              <span className="text-xs text-gray-500">
                Modified: {formatDateLocale(template.lastModified)}
                {template.modifiedBy && ` by ${template.modifiedBy}`}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={isSaving}
              data-testid="save-patient-template-btn"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Preview Modal (Patient-specific)
const PatientPreviewModal = ({ template, onClose }) => {
  // Generate preview with patient mock data
  const preview = useMemo(() => {
    let previewSubject = template.subject;
    let previewBody = template.body;
    
    Object.entries(MOCK_PATIENT_DATA).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      previewSubject = previewSubject.replace(regex, value);
      previewBody = previewBody.replace(regex, value);
    });
    
    return { subject: previewSubject, body: previewBody };
  }, [template.subject, template.body]);
  
  const categoryConfig = PATIENT_CATEGORIES[template.category] || PATIENT_CATEGORIES.auth;
  const CategoryIcon = categoryConfig.icon;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`p-5 flex items-center justify-between ${categoryConfig.color}`}>
          <div className="flex items-center gap-3">
            <CategoryIcon className="h-5 w-5" />
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-bold">{template.name}</h2>
                <Badge variant="outline" className="text-xs bg-white/50">
                  Patient Email
                </Badge>
              </div>
              <p className="text-sm opacity-80">{template.description}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="hover:bg-black/10">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4 flex items-center justify-between">
            <span className="text-sm text-gray-500">Sample preview for: <strong>Maria Garcia</strong></span>
            <Badge className="bg-slate-100 text-slate-700 border border-slate-300" data-testid="patient-template-sample-badge">Sample preview</Badge>
          </div>
          
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            {/* Email Header */}
            <div className="bg-gray-100 p-3 border-b space-y-1">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="font-medium">From:</span>
                <span>TreatX &lt;notifications@treatx.health&gt;</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="font-medium">To:</span>
                <span>{MOCK_PATIENT_DATA.patientEmail}</span>
              </div>
              <div className="flex items-center gap-2 text-sm pt-1 border-t border-gray-200">
                <span className="font-medium text-gray-600">Subject:</span>
                <span className="font-semibold text-gray-900">{preview.subject}</span>
              </div>
            </div>
            
            {/* Email Body */}
            <div className="p-4">
              <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed">
                {preview.body}
              </pre>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>Event Key:</strong>{' '}
              <code className="bg-blue-100 px-1 rounded">{template.eventKey}</code>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-between items-center">
          <p className="text-xs text-gray-500">
            This template will be triggered by: <strong>{template.triggerSource}</strong> events
          </p>
          <Button onClick={onClose}>Close Preview</Button>
        </div>
      </div>
    </div>
  );
};

// Main Content Component
const PatientEmailTemplatesContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { admin, logout } = useTreatXAdminAuth();
  const { 
    getTemplatesByAudience, 
    toggleTemplateStatus, 
    updateTemplate 
  } = useEmailTemplates();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(['auth', 'intake', 'matching', 'offer', 'payment']);

  // 11C: live per-template status from the canonical
  // /admin/notifications/template-status endpoint (read-only).
  const { statusByEventKey } = useTemplateStatusMap();

  // Redirect if not logged in
  useEffect(() => {
    if (!admin) {
      navigate('/treatx-admin/login');
    }
  }, [admin, navigate]);

  if (!admin) return null;

  // Get ONLY patient templates (strict filtering)
  const patientTemplates = getTemplatesByAudience(EMAIL_AUDIENCE.PATIENT);

  // Filter by search
  const filteredTemplates = searchQuery
    ? patientTemplates.filter(tpl => 
        tpl.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tpl.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tpl.eventKey.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : patientTemplates;

  // Group by category
  const groupedTemplates = filteredTemplates.reduce((acc, tpl) => {
    const cat = tpl.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(tpl);
    return acc;
  }, {});

  // Stats
  const stats = {
    total: patientTemplates.length,
    active: patientTemplates.filter(t => t.isActive).length,
    customized: patientTemplates.filter(t => !t.isDefault).length,
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => 
      prev.includes(cat) 
        ? prev.filter(c => c !== cat)
        : [...prev, cat]
    );
  };

  const handleLogout = () => {
    logout();
    navigate('/treatx-admin/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-slate-50">
      <Toaster />
      
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-700 to-cyan-600 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/treatx-admin/email-templates')}
                className="text-white hover:bg-white/20"
                data-testid="back-to-templates-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2" data-testid="patient-templates-title">
                  Patient Email Templates
                  <Badge className="bg-blue-800/50 text-blue-100 text-xs">
                    Event-Driven
                  </Badge>
                </h1>
                <p className="text-sm text-blue-200">Manage patient communication templates</p>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300 ml-2" data-testid="patient-templates-header-backend-badge">BACKEND-CONNECTED</Badge>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <img 
                  src={admin.avatar} 
                  alt={admin.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="hidden sm:block">
                  <p className="text-sm font-medium">{admin.name}</p>
                  <p className="text-xs text-blue-200">{admin.title}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-blue-200 hover:text-white hover:bg-white/10"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Info Banner */}
      <div className="bg-blue-100 border-b border-blue-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-blue-800">
            <Shield className="h-4 w-4" />
            <p className="text-sm">
              <strong>Patient Templates Only:</strong> This view shows templates for patient communication. 
              Event keys are immutable – you can only edit subject and body content.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
                <p className="text-xs text-slate-500">Patient Events</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
                <p className="text-xs text-slate-500">Active</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-600">{stats.customized}</p>
                <p className="text-xs text-slate-500">Customized</p>
              </div>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search patient templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-72"
                data-testid="search-patient-templates"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Category Groups */}
        <div className="space-y-4">
          {Object.entries(PATIENT_CATEGORIES).map(([categoryKey, categoryConfig]) => {
            const CategoryIcon = categoryConfig.icon;
            const categoryTemplates = groupedTemplates[categoryKey] || [];
            const isExpanded = expandedCategories.includes(categoryKey);
            
            if (categoryTemplates.length === 0 && searchQuery) return null;
            
            return (
              <Card key={categoryKey} className={`border-2 ${categoryConfig.borderColor}`}>
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(categoryKey)}
                  className="w-full"
                >
                  <CardHeader className={`${categoryConfig.color} cursor-pointer hover:opacity-90 transition-opacity`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CategoryIcon className="h-5 w-5" />
                        <div className="text-left">
                          <CardTitle className="text-base">{categoryConfig.label}</CardTitle>
                          <CardDescription className="text-xs opacity-80">
                            {categoryConfig.description}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-white/50">
                          {categoryTemplates.length} templates
                        </Badge>
                        <span className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                          ▼
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                </button>
                
                {/* Templates List */}
                {isExpanded && (
                  <CardContent className="p-4">
                    {categoryTemplates.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No templates in this category match your search
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {categoryTemplates.map(template => (
                          <div 
                            key={template.id}
                            className={`border rounded-lg p-4 transition-all hover:shadow-md ${
                              template.isActive ? 'bg-white' : 'bg-gray-50 opacity-75'
                            }`}
                            data-testid={`patient-template-${template.eventKey}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <h3 className="font-semibold text-gray-900">{template.name}</h3>
                                  {!template.isDefault && (
                                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                      Customized
                                    </Badge>
                                  )}
                                  {!template.isActive && (
                                    <Badge variant="outline" className="text-xs bg-gray-100 text-gray-500">
                                      Disabled
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500 mb-1">{template.description}</p>
                                <div className="flex items-center gap-2">
                                  <Lock className="h-3 w-3 text-gray-400" />
                                  <code className="text-xs text-gray-400 font-mono">
                                    {template.eventKey}
                                  </code>
                                </div>
                                {/* 11C inline status indicators */}
                                <TemplateInlineIndicators
                                  eventKey={template.eventKey}
                                  isActive={template.isActive}
                                  subject={template.subject}
                                  body={template.body}
                                  allowedVariables={template.defaultVariables}
                                  serverStatus={statusByEventKey[template.eventKey] || null}
                                />
                              </div>
                              
                              {/* Actions */}
                              <div className="flex items-center gap-3 ml-4">
                                {/* Active Toggle */}
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500">
                                    {template.isActive ? 'Active' : 'Disabled'}
                                  </span>
                                  <Switch
                                    checked={template.isActive}
                                    onCheckedChange={() => toggleTemplateStatus(template.id)}
                                    data-testid={`toggle-${template.eventKey}`}
                                  />
                                </div>
                                
                                {/* View Button */}
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setPreviewTemplate(template)}
                                  data-testid={`preview-${template.eventKey}`}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  View
                                </Button>
                                
                                {/* Edit Button */}
                                <Button 
                                  size="sm"
                                  onClick={() => setSelectedTemplate(template)}
                                  className="bg-blue-600 hover:bg-blue-700"
                                  data-testid={`edit-${template.eventKey}`}
                                >
                                  <Edit2 className="h-4 w-4 mr-1" />
                                  Edit
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {/* No Results */}
        {filteredTemplates.length === 0 && (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No patient templates match your search</p>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-6 p-4 bg-white border rounded-lg shadow-sm">
          <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Info className="h-4 w-4 text-blue-600" />
            Patient Email Lifecycle
          </h4>
          <div className="grid grid-cols-5 gap-3 text-sm">
            {Object.entries(PATIENT_CATEGORIES).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <div key={key} className={`p-3 rounded-lg ${config.color}`}>
                  <Icon className="h-4 w-4 mb-1" />
                  <p className="font-medium text-xs">{config.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      {/* Edit Modal */}
      {selectedTemplate && (
        <PatientTemplateEditorModal
          template={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
          onSave={updateTemplate}
        />
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <PatientPreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </div>
  );
};

// Wrapper with provider
const PatientEmailTemplates = () => {
  return (
    <EmailTemplateProvider>
      <PatientEmailTemplatesContent />
    </EmailTemplateProvider>
  );
};

export default PatientEmailTemplates;
